// =============================================================================
// Payee Remit Details (Dynamis receives payment here)
// =============================================================================
//
// The bank-transfer details a client uses to PAY Dynamis. Same for every client
// (the payee is always Dynamis), so it lives here as a constant — NOT in per-org
// settings (that's billing_info, the CLIENT) and NOT editable in the UI.
//
// This is the USD receiving balance of the official "Dynamis Revenue Pty Ltd" Wise
// business account. It REPLACES the old personal "Nathnaiel Greengrass" account as of
// Jul 2026 — the company invoices now, not Nathaniel. account_holder is the exact legal
// name on the Wise account and is a matching key against Wise's records, not free text —
// keep it byte-for-byte as shown on the account.
// Rendered on the client invoice PDF only when the "Include Wise details" toggle
// is on (off by default).
export const DYNAMIS_WISE_REMIT = {
  account_holder: "Dynamis Revenue Pty Ltd",
  routing_number: "084009519", // ACH / wire — use when sending from the US
  account_number: "198143713701591",
  swift_bic: "TRWIUS35XXX", // use when sending from outside the US
  bank_address: "Wise US Inc, 108 W 13th St, Wilmington, DE 19801, USA",
} as const;

// =============================================================================
// Display-currency conversion rates (per-user USD ⇄ AUD/SGD/…)
// =============================================================================
// Every dollar in the DB is USD (canonical — Dynamis banks a USD Wise balance).
// A user's profiles.display_currency picks which currency they SEE; this map is
// the rate for that display multiply. USD is implicit (rate 1) and never listed.
//
// Hardcoded on purpose: no DB table, no infra, no live FX (CSP blocks it). To
// add or update a currency: edit this map + set the user's display_currency.
// That's the whole workflow — no migration needed to onboard a new currency.
export interface FxRate {
  rate: number; // 1 USD = `rate` units of this currency
  asOf: string; // ISO date, shown under the toggle for transparency
}

export const FX_RATES: Record<string, FxRate> = {
  AUD: { rate: 1.53, asOf: "2026-07-15" },
  SGD: { rate: 1.29, asOf: "2026-07-16" },
};

/** Rate for a display-currency code. USD (or any unknown code) → 1 (no convert). */
export function fxRate(code: string | null | undefined): number {
  return code && FX_RATES[code] ? FX_RATES[code].rate : 1;
}

// =============================================================================
// Invoice Payment Terms
// =============================================================================
//
// Days from the invoice date (= the billing-period end, per the accountant's dating
// rule) until payment is due. ONE source of truth: the client invoice PDF, the
// on-screen preview, and InvoicePage's due-date derive all read this — so the printed
// "Due N days" terms can never contradict the printed Due Date.
export const INVOICE_PAYMENT_TERMS_DAYS = 7;

// =============================================================================
// Phone-Setter (Dialer) Commission — pay-model dates
// =============================================================================
//
// Phone setters (Altara role `dialer`, e.g. Rayston's Alyssa + Samantha) moved
// from salary to commission on 2026-08-10. Base pay and commission NEVER stack on
// the same day: a period day on or after this date pays commission; a day before it
// pays prorated base. So an Aug 1-15 invoice splits — Aug 1-9 = base, Aug 10-15 =
// commission. A period wholly before this date is base only; wholly after is
// commission only ($0 base). SSOT date so useInvoice + payout can't disagree.
// Doctrine: /phonepay + [dynamis]_PHONE_SETTER_PAY.md.
export const DIALER_COMMISSION_START_DATE = "2026-08-10";

// The one-time transition half-month (Aug 10-15, 2026). During it an SCO counts
// for commission ONLY if BOTH its own call_start_time AND event_date fall inside
// the window — call_start_time keeps out pre-Aug-10 work (before commission),
// event_date keeps out late cash the next invoice pays (e.g. Nelson Chee: call
// Aug 15, cash Aug 16 → belongs on the Aug 16-31 invoice). Any commission window
// ending on/before this date uses the both-dates filter; Aug 16+ uses event_date.
export const DIALER_TRANSITION_END_DATE = "2026-08-15";

// =============================================================================
// N8N Webhook Configuration
// =============================================================================

export const N8N_WEBHOOKS = {
  // Master Admin Workflow (Handles: Save, Update, Delete for ALL Admin actions)
  ADMIN_WORKFLOW: "https://dynamisrev.cloud/webhook/calendly-hook",

  // Public Workflow for Careers/Client Applications (Unauthenticated)
  APPLY_WORKFLOW: "https://dynamisrev.cloud/webhook/application-hook",

  // Altara Omnia Workflows (Document Worker)
  ALTARA_OMNIA_WORKFLOW: "https://dynamisrev.cloud/webhook/altara-omnia",
  ALTARA_OMNIA_DELETE_WEBHOOK: "https://dynamisrev.cloud/webhook/omnia-deletion",

  // ARCHIVED (Feb 2026): Send DM feature disabled — workflow deactivated. Kept for revival.
  ALTARA_GHL_SEND_MESSAGE: "https://dynamisrev.cloud/webhook/ghl-send-message",

  // Billing - Organization billing info updates
  ALTARA_BILLING_WORKFLOW: "https://dynamisrev.cloud/webhook/altara-billing",

  // Client Portal - Task/File/Communication CRUD
  ALTARA_CLIENT_PORTAL: "https://dynamisrev.cloud/webhook/altara-client-portal",

  // Post-Call Outcome — Calendar outcome submission. Routes via webhook-inbox to the
  // PROVEN Google Form workflow (post-call-gform, 466/466 forwarded, 0 failed) — the SAME
  // pipeline real Google Form submissions use. The old altara/post_call_outcome route
  // (→ post-call-outcome-v2) died at a dead "Append to Sheet" Google-cred node UPSTREAM of
  // the Supabase insert, silently dropping every outcome (May 31 2026 incident). The Edge
  // Function forwards only the payload body, so the gform workflow processes our payload
  // identically — which is why PostCallOutcomeModal's payload MUST match the Google Form
  // field shape byte-for-byte (see PostCallOutcomePayload + handleSubmit).
  ALTARA_POST_CALL_OUTCOME:
    "https://szytmkujyyzbrmeqebeb.supabase.co/functions/v1/webhook-inbox?source=google_form&client=rayston&workflow=post_call",

  // GHL Conversation Resync — Dedicated single-conversation sync from GHL to Supabase
  ALTARA_GHL_CONVERSATION_RESYNC: "https://dynamisrev.cloud/webhook/ghl-conversation-resync",

  // DEPRECATED (Feb 2026): Original resync webhook, replaced by ALTARA_GHL_CONVERSATION_RESYNC
  ALTARA_GHL_RESYNC_CONVERSATION: "https://dynamisrev.cloud/webhook/ghl-resync-conversation",

  // Lead Identity Review — Confirm/reject lead matches
  ALTARA_LEAD_MATCH_REVIEW: "https://dynamisrev.cloud/webhook/lead-match-review",

  // Zoom API Router — List webinars, get participants, sync attendance, save reports
  ALTARA_ZOOM_API: "https://dynamisrev.cloud/webhook/zoom-api",

  // Message Pattern Classification — Classify ManyChat vs human scripts
  ALTARA_MESSAGE_PATTERN: "https://dynamisrev.cloud/webhook/message-pattern",

  // Abandoned Payment Plan — Slack notification when a lead is marked abandoned
  ALTARA_ABANDONED_PAYMENT_PLAN: "https://dynamisrev.cloud/webhook/abandoned-payment-plan",

  // Manual Cancel — Calendar page manual call cancellation → Slack + Supabase
  ALTARA_MANUAL_CANCEL: "https://dynamisrev.cloud/webhook/manual-cancel",

  // Stub — N8N workflow not yet built. Edge function will no-op if this constant is empty.
  // When ready, create N8N workflow that posts {org_name, client_email, slug} to Slack.
  ALTARA_CLIENT_ONBOARDED: "", // TODO: wire N8N workflow

  // Client Portal - Task Slack Notification. SERVER-DRIVEN, not frontend-fired:
  // dispatch_portal_task_notifications() (pg_cron, every minute) POSTs to this URL
  // ONE coalesced message per task after it sits untouched 5 min (create/edit/move/
  // complete/subtask/comment all debounce into one). See migration
  // sql/migrations/2026-06-02_portal_task_debounced_notifications.sql.
  // N8N workflow "Portal Task Created → Slack" (id qs0RnVoaeBc37iSq) looks up
  // organizations.slack_channel_id by organization_id and posts to that org's
  // Dynamis-workspace channel. NULL channel = no-op. Debounced payload:
  // { organization_id, task_id, title, status, due_date, priority,
  //   event_kinds[], comment_count, last_actor_name }.
  // Constant retained as documentation of the webhook the cron dispatch targets.
  ALTARA_PORTAL_TASK_CREATED: "https://dynamisrev.cloud/webhook/altara-portal-task-created",
};
